import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ClipboardList, Plus, RefreshCw, Clock, AlertCircle, AlertTriangle,
  X, Loader2, MessageSquare, Paperclip, Activity, MapPin, Building2,
  User, Users, Calendar, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permission constants
const WORK_ORDER_MANAGE_ROLES = ['project_manager', 'operations_manager', 'admin', 'super_admin'];

// Status configuration
const STATUS_CONFIG = {
  open: { label: 'Open', bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/50' },
  scheduled: { label: 'Scheduled', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/50' },
  in_progress: { label: 'In Progress', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/50' },
  blocked: { label: 'Blocked', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50' },
  completed: { label: 'Completed', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/50' },
  closed: { label: 'Closed', bg: 'bg-slate-700/30', text: 'text-slate-500', border: 'border-slate-700' },
  canceled: { label: 'Canceled', bg: 'bg-slate-700/30', text: 'text-slate-500', border: 'border-slate-700 line-through' },
};

// Priority configuration
const PRIORITY_CONFIG = {
  low: { label: 'Low', text: 'text-slate-400', icon: null },
  medium: { label: 'Medium', text: 'text-blue-400', icon: null },
  high: { label: 'High', text: 'text-amber-400', icon: AlertCircle },
  urgent: { label: 'Urgent', text: 'text-red-400 font-bold', icon: AlertTriangle },
};

// Category options
const CATEGORIES = [
  { value: 'repair', label: 'Repair' },
  { value: 'punch', label: 'Punch' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'service', label: 'Service' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'ops_task', label: 'Ops Task' },
];

// Status transitions
const STATUS_TRANSITIONS = {
  open: ['scheduled', 'in_progress', 'canceled'],
  scheduled: ['in_progress', 'open', 'canceled'],
  in_progress: ['completed', 'blocked', 'canceled'],
  blocked: ['in_progress', 'canceled'],
  completed: ['closed', 'in_progress'],
  closed: [],
  canceled: [],
};

// Helper functions
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
};

const isOverdue = (dueDate, status) => {
  if (!dueDate || ['completed', 'closed', 'canceled'].includes(status)) return false;
  return new Date(dueDate) < new Date();
};

// Badge components
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <Badge className={`${config.bg} ${config.text} ${config.border} border`}>
      {config.label}
    </Badge>
  );
};

const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  const Icon = config.icon;
  return (
    <span className={`flex items-center gap-1 ${config.text}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {config.label}
    </span>
  );
};


export const WorkOrdersTab = ({ project, onRefresh, refreshKey }) => {
  const { user } = useAuth();
  
  // Data state
  const [workOrders, setWorkOrders] = useState([]);
  const [crews, setCrews] = useState([]);
  const [users, setUsers] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'ops_task',
    assigned_crew_id: '',
    assigned_user_id: '',
    due_date: '',
  });
  
  // Detail data
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteIsInternal, setNoteIsInternal] = useState(false);
  
  // Permission checks
  const canManage = user?.role && WORK_ORDER_MANAGE_ROLES.includes(user.role.toLowerCase());
  
  // Fetch work orders for this project
  const fetchWorkOrders = useCallback(async () => {
    if (!project?.id) return;
    
    setLoading(true);
    try {
      const [woRes, crewsRes] = await Promise.all([
        axios.get(`${API}/ops/work-orders?job_id=${project.id}`),
        axios.get(`${API}/ops/crews`).catch(() => ({ data: { crews: [] } })),
      ]);
      
      setWorkOrders(woRes.data.work_orders || []);
      setCrews(crewsRes.data.crews || []);
    } catch (err) {
      console.error('Error fetching work orders:', err);
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);
  
  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders, refreshKey]);
  
  // Fetch users for assignment
  useEffect(() => {
    const fetchUsers = async () => {
      if (canManage) {
        try {
          const res = await axios.get(`${API}/users`);
          setUsers(res.data || []);
        } catch (err) {
          console.error('Error fetching users:', err);
        }
      }
    };
    fetchUsers();
  }, [canManage]);
  
  // Fetch detail data
  useEffect(() => {
    const fetchDetailData = async () => {
      if (!selectedWorkOrder) return;
      
      try {
        const [notesRes, activityRes] = await Promise.all([
          axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/notes`),
          axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/activity`),
        ]);
        
        setNotes(notesRes.data.notes || []);
        setActivity(activityRes.data.activities || []);
      } catch (err) {
        console.error('Error fetching detail data:', err);
      }
    };
    
    if (detailSheetOpen && selectedWorkOrder) {
      fetchDetailData();
    }
  }, [selectedWorkOrder, detailSheetOpen]);
  
  // Open detail
  const openDetail = (wo) => {
    setSelectedWorkOrder(wo);
    setActiveTab('overview');
    setDetailSheetOpen(true);
  };
  
  // Create work order
  const handleCreate = async () => {
    if (!createForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: createForm.title.trim(),
        description: createForm.description.trim() || null,
        priority: createForm.priority,
        category: createForm.category,
        job_id: project.id,
        assigned_crew_id: createForm.assigned_crew_id || null,
        assigned_user_id: createForm.assigned_user_id || null,
        due_date: createForm.due_date || null,
      };
      
      const res = await axios.post(`${API}/ops/work-orders`, payload);
      toast.success(`Work order ${res.data.work_order_number} created`);
      setCreateModalOpen(false);
      resetCreateForm();
      fetchWorkOrders();
    } catch (err) {
      console.error('Error creating work order:', err);
      toast.error(err.response?.data?.detail || 'Failed to create work order');
    } finally {
      setSaving(false);
    }
  };
  
  const resetCreateForm = () => {
    setCreateForm({
      title: '',
      description: '',
      priority: 'medium',
      category: 'ops_task',
      assigned_crew_id: '',
      assigned_user_id: '',
      due_date: '',
    });
  };
  
  // Update status
  const handleStatusChange = async (newStatus) => {
    if (!selectedWorkOrder) return;
    
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/ops/work-orders/${selectedWorkOrder.id}`, { status: newStatus });
      setSelectedWorkOrder(res.data);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchWorkOrders();
      
      const activityRes = await axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/activity`);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };
  
  // Add note
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedWorkOrder) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/ops/work-orders/${selectedWorkOrder.id}/notes`, {
        body: newNote.trim(),
        is_internal: noteIsInternal,
      });
      toast.success('Note added');
      setNewNote('');
      setNoteIsInternal(false);
      
      const [notesRes, activityRes] = await Promise.all([
        axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/notes`),
        axios.get(`${API}/ops/work-orders/${selectedWorkOrder.id}/activity`),
      ]);
      setNotes(notesRes.data.notes || []);
      setActivity(activityRes.data.activities || []);
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-400">Loading work orders...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="work-orders-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Work Orders</h3>
          <p className="text-sm text-slate-400">Service, repair, and follow-up work for this project</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchWorkOrders} className="border-slate-600 text-slate-300">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canManage && (
            <Button onClick={() => setCreateModalOpen(true)} size="sm" className="bg-blue-600 hover:bg-blue-500" data-testid="create-wo-project-btn">
              <Plus className="h-4 w-4 mr-1" />
              New Work Order
            </Button>
          )}
        </div>
      </div>

      {/* Work Orders List */}
      {workOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-slate-700 rounded-lg">
          <ClipboardList className="h-12 w-12 text-slate-600 mb-3" />
          <h4 className="text-white font-medium mb-1">No work orders for this project yet</h4>
          <p className="text-sm text-slate-400 mb-4">Create a work order to track service or repair work</p>
          {canManage && (
            <Button onClick={() => setCreateModalOpen(true)} className="bg-blue-600 hover:bg-blue-500">
              <Plus className="h-4 w-4 mr-1" />
              New Work Order
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800/80">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">WO #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Assigned</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Updated</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map(wo => (
                <tr
                  key={wo.id}
                  onClick={() => openDetail(wo)}
                  className="border-t border-slate-700/50 hover:bg-slate-700/30 cursor-pointer"
                  data-testid={`wo-tab-row-${wo.work_order_number}`}
                >
                  <td className="px-4 py-3 text-sm font-mono text-blue-400">{wo.work_order_number}</td>
                  <td className="px-4 py-3 text-sm text-slate-300 max-w-[200px] truncate">{wo.title}</td>
                  <td className="px-4 py-3 text-sm"><PriorityBadge priority={wo.priority} /></td>
                  <td className="px-4 py-3 text-sm"><StatusBadge status={wo.status} /></td>
                  <td className="px-4 py-3 text-sm text-slate-300">{wo.assigned_crew_name || wo.assigned_user_name || '—'}</td>
                  <td className={`px-4 py-3 text-sm ${isOverdue(wo.due_date, wo.status) ? 'text-red-400' : 'text-slate-400'}`}>
                    {formatDate(wo.due_date)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{formatRelativeTime(wo.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-300 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              New Work Order
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a work order for {project?.name || project?.customer_name || 'this project'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title *</Label>
              <Input
                value={createForm.title}
                onChange={(e) => setCreateForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Repair damaged soffit panels"
                className="bg-slate-700 border-slate-600"
                data-testid="wo-tab-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the work to be done..."
                className="bg-slate-700 border-slate-600 min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Priority</Label>
                <Select value={createForm.priority} onValueChange={(v) => setCreateForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Select value={createForm.category} onValueChange={(v) => setCreateForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {canManage && crews.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-300">Assign to Crew</Label>
                <Select value={createForm.assigned_crew_id || 'none'} onValueChange={(v) => setCreateForm(f => ({ ...f, assigned_crew_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select crew" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="none">No crew</SelectItem>
                    {crews.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">Due Date</Label>
              <Input
                type="date"
                value={createForm.due_date}
                onChange={(e) => setCreateForm(f => ({ ...f, due_date: e.target.value }))}
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)} className="border-slate-600" disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving || !createForm.title} className="bg-blue-600 hover:bg-blue-500" data-testid="submit-create-wo-tab">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-[500px] bg-slate-800 border-l border-slate-700 text-slate-300 overflow-y-auto">
          {selectedWorkOrder && (
            <>
              <SheetHeader className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <SheetTitle className="text-lg text-white">{selectedWorkOrder.title}</SheetTitle>
                    <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
                      <MapPin className="h-3 w-3" />
                      <span>{selectedWorkOrder.market_name}</span>
                    </div>
                  </div>
                  <Badge className="font-mono bg-slate-700 text-slate-300 text-xs">{selectedWorkOrder.work_order_number}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <PriorityBadge priority={selectedWorkOrder.priority} />
                  <StatusBadge status={selectedWorkOrder.status} />
                </div>
              </SheetHeader>

              <Separator className="my-4 bg-slate-700" />

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-slate-900/50 p-1 rounded-lg border border-slate-700/50 w-full grid grid-cols-3">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 text-xs">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 text-xs">
                    Notes ({notes.length})
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 text-xs">
                    Activity
                  </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-4 space-y-3">
                  {canManage && STATUS_TRANSITIONS[selectedWorkOrder.status]?.length > 0 && (
                    <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-700">
                      <Label className="text-xs text-slate-400 mb-2 block">Change Status</Label>
                      <div className="flex flex-wrap gap-2">
                        {STATUS_TRANSITIONS[selectedWorkOrder.status].map(newStatus => (
                          <Button
                            key={newStatus}
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusChange(newStatus)}
                            disabled={saving}
                            className="border-slate-600 text-xs"
                          >
                            {STATUS_CONFIG[newStatus]?.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 bg-slate-700/30 rounded-lg">
                      <Label className="text-xs text-slate-500 uppercase">Assigned Crew</Label>
                      <div className="mt-1 flex items-center gap-1 text-sm text-white">
                        <Users className="h-3 w-3 text-slate-400" />
                        {selectedWorkOrder.assigned_crew_name || '—'}
                      </div>
                    </div>
                    <div className="p-2 bg-slate-700/30 rounded-lg">
                      <Label className="text-xs text-slate-500 uppercase">Due Date</Label>
                      <div className={`mt-1 flex items-center gap-1 text-sm ${isOverdue(selectedWorkOrder.due_date, selectedWorkOrder.status) ? 'text-red-400' : 'text-white'}`}>
                        <Calendar className="h-3 w-3 text-slate-400" />
                        {formatDate(selectedWorkOrder.due_date)}
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-slate-700/30 rounded-lg space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Category</span>
                      <span className="text-white capitalize">{selectedWorkOrder.category?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Created</span>
                      <span className="text-white">{formatDateTime(selectedWorkOrder.created_at)}</span>
                    </div>
                  </div>

                  {selectedWorkOrder.description && (
                    <div className="p-2 bg-slate-700/30 rounded-lg">
                      <Label className="text-xs text-slate-500 uppercase mb-1 block">Description</Label>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{selectedWorkOrder.description}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="mt-4 space-y-3">
                  {canManage && (
                    <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-700 space-y-2">
                      <Textarea
                        placeholder="Add a note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="bg-slate-700 border-slate-600 min-h-[60px] text-sm"
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch checked={noteIsInternal} onCheckedChange={setNoteIsInternal} />
                          <Label className="text-xs text-slate-400">Internal</Label>
                        </div>
                        <Button onClick={handleAddNote} disabled={!newNote.trim() || saving} size="sm" className="bg-blue-600 hover:bg-blue-500">
                          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {notes.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 text-sm">
                      <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p>No notes yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {notes.map(note => (
                        <div key={note.id} className="p-2 bg-slate-700/30 rounded-lg border border-slate-700">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-3 w-3 text-slate-400" />
                            <span className="text-xs font-medium text-white">{note.author_name || 'User'}</span>
                            <span className="text-xs text-slate-500">{formatRelativeTime(note.created_at)}</span>
                            {note.is_internal && (
                              <Badge className="text-xs bg-slate-600 text-slate-300 py-0">Internal</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-300">{note.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="mt-4 space-y-2">
                  {activity.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 text-sm">
                      <Activity className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p>No activity yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {activity.map(act => (
                        <div key={act.id} className="flex gap-2 p-2 bg-slate-700/30 rounded-lg border border-slate-700">
                          <div className="h-6 w-6 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                            <Activity className="h-3 w-3 text-slate-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-white">
                              {act.event_type === 'created' && 'Work order created'}
                              {act.event_type === 'status_changed' && (
                                <>Status: <span className="text-slate-400">{act.old_value}</span> → <span className="text-blue-400">{act.new_value}</span></>
                              )}
                              {act.event_type === 'assigned' && `Assigned to ${act.new_value}`}
                              {act.event_type === 'note_added' && 'Note added'}
                              {act.event_type === 'closed' && 'Closed'}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {act.actor_name || 'System'} • {formatRelativeTime(act.created_at)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default WorkOrdersTab;
